/**
 * A product's condition: new, used, refurbished, or the shop's own words.
 *
 * Bagz Phones, 31 Oct 2026: "a dedicated product condition attribute with
 * options such as: Brand New; UK Premium (Used) ... customers can easily
 * identify whether a phone is new or used when viewing products on the
 * website ... filtering much easier, particularly when we have the same
 * phone model available in both conditions."
 *
 * `products.condition` has always been free text in the database; the form
 * offered only "New" and "Second Hand". It now offers a few common labels,
 * the labels the shop has used before, and lets the shop type its own
 * ("UK Premium (Used)", "Ex-Dubai"). Stored values keep their spelling:
 * "New" and "Second Hand" are still "New" and "Second Hand".
 *
 * On the website:
 *   - plain "New" (and no condition at all, which every product saved before
 *     the form sent one has) shows nothing extra, exactly as before, so no
 *     shop's site changes by itself;
 *   - any other label shows on the product's photo and on its page —
 *     "Brand New", "UK Premium (Used)", "Second Hand";
 *   - the Condition filter appears once the shop sells two or more.
 *
 * Pure and dependency-free: the Convex shop queries import it by relative
 * path, the product form and the storefront by `@/`.
 */

/** What a product with no condition is: what the form has always saved. */
export const DEFAULT_CONDITION = "New";

/** Longer than this is cut (a badge on a phone-width card has to fit). */
export const MAX_CONDITION_LENGTH = 40;

export interface ConditionPreset {
  label: string;
  /** The line under it in the form's picker. */
  hint: string;
}

/** The common labels, in the order the form and the filter list them. */
export const CONDITION_PRESETS: readonly ConditionPreset[] = [
  { label: "New", hint: "The usual. Your website shows nothing extra." },
  { label: "Brand New", hint: "Your website shows Brand New on the photo." },
  { label: "Second Hand", hint: "Used before. Your website shows it on the photo." },
  { label: "Refurbished", hint: "Repaired or restored. Your website shows it on the photo." },
];

/**
 * A condition as it is stored: spaces tidied, cut to MAX_CONDITION_LENGTH,
 * and a preset typed in any case given the preset's own spelling ("second
 * hand" → "Second Hand"). Blank or not text → undefined.
 */
export function cleanCondition(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const tidy = raw.replace(/\s+/g, " ").trim().slice(0, MAX_CONDITION_LENGTH).trim();
  if (!tidy) return undefined;
  const preset = CONDITION_PRESETS.find((p) => p.label.toLowerCase() === tidy.toLowerCase());
  return preset ? preset.label : tidy;
}

/** A product's condition label — New when it has none. */
export function conditionOf(product: { condition?: unknown } | null | undefined): string {
  return cleanCondition(product?.condition) ?? DEFAULT_CONDITION;
}

/** Two labels name the same condition (case and spacing ignored). Blank never matches. */
export function sameCondition(a: unknown, b: unknown): boolean {
  const x = cleanCondition(a);
  const y = cleanCondition(b);
  return !!x && !!y && x.toLowerCase() === y.toLowerCase();
}

/** Does this product pass the website's Condition filter for `wanted`? */
export function matchesCondition(product: { condition?: unknown } | null | undefined, wanted: unknown): boolean {
  return sameCondition(conditionOf(product), wanted);
}

/** The colour family a label's badge is drawn in. */
export type ConditionTone = "new" | "used" | "refurbished" | "other";

const REFURBISHED = /\b(refurb\w*|renewed|restored|reconditioned)\b/i;
const USED =
  /\b(used|second[\s-]?hand|pre[\s-]?owned|ex[\s-]?(uk|us|usa|dubai|uae|japan|china|europe)|uk[\s-]?premium|open[\s-]?box|like[\s-]?new|grade\s?[a-d])\b/i;
const NEW = /\b(new|sealed|unused|boxed)\b/i;

/** New (green), used (amber), refurbished (blue), or anything else (grey). */
export function conditionTone(label: unknown): ConditionTone {
  const s = cleanCondition(label);
  if (!s) return "new";
  if (REFURBISHED.test(s)) return "refurbished";
  if (USED.test(s)) return "used";
  if (NEW.test(s)) return "new";
  return "other";
}

/**
 * The condition the website shows for a product, or null for none: plain New
 * (or no condition) shows nothing, and neither does a service, a room, a car
 * or a property listing — they have their own details.
 */
export function websiteConditionLabel(product: any): string | null {
  if (!product) return null;
  if (product.kind && product.kind !== "product") return null;
  if (product.motors || product.property) return null;
  const label = conditionOf(product);
  return label.toLowerCase() === DEFAULT_CONDITION.toLowerCase() ? null : label;
}

/**
 * The conditions a set of products comes in, for the website's Condition
 * filter: one entry per condition (case ignored, the first spelling met
 * kept), the presets in their own order first, then the shop's own labels,
 * the most used first. A product with no condition counts as New.
 */
export function conditionFacet(products: ReadonlyArray<{ condition?: unknown } | null | undefined>): string[] {
  const counts = new Map<string, { label: string; count: number }>();
  for (const p of products) {
    if (!p) continue;
    const label = conditionOf(p);
    const key = label.toLowerCase();
    const row = counts.get(key);
    if (row) row.count += 1;
    else counts.set(key, { label, count: 1 });
  }
  const presetRank = (label: string) => {
    const i = CONDITION_PRESETS.findIndex((p) => p.label.toLowerCase() === label.toLowerCase());
    return i < 0 ? Number.POSITIVE_INFINITY : i;
  };
  return [...counts.values()]
    .sort((a, b) => {
      const ra = presetRank(a.label);
      const rb = presetRank(b.label);
      if (ra !== rb) return ra - rb;
      if (a.count !== b.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    })
    .map((r) => r.label);
}

/**
 * The labels the product form offers: the presets, then the labels this shop
 * already uses, then ones typed recently on this device, then the product's
 * own when it is none of those. One of each (case ignored).
 */
export function conditionChoices(
  current: unknown,
  shopLabels: ReadonlyArray<unknown> = [],
  recent: ReadonlyArray<unknown> = [],
): { label: string; own: boolean }[] {
  const out: { label: string; own: boolean }[] = [];
  const seen = new Set<string>();
  const add = (raw: unknown, own: boolean) => {
    const label = cleanCondition(raw);
    if (!label || seen.has(label.toLowerCase())) return;
    seen.add(label.toLowerCase());
    out.push({ label, own });
  };
  for (const p of CONDITION_PRESETS) add(p.label, false);
  for (const s of shopLabels) add(s, true);
  for (const r of recent) add(r, true);
  add(current, true);
  return out;
}

/** The picker's line under a label. */
export function conditionHint(label: string): string {
  const preset = CONDITION_PRESETS.find((p) => p.label.toLowerCase() === label.toLowerCase());
  return preset ? preset.hint : "Your own label. Your website shows it on the photo.";
}
